import React, { Component } from "react";
import { Button, Form, Label, Message, Search } from "semantic-ui-react";
import PlacesAutocomplete from "react-places-autocomplete";
import {
  geocodeByAddress,
  geocodeByPlaceId,
  getLatLng,
} from "react-places-autocomplete";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { stateTaxOptions } from "../../common";
import { usStates } from "../../common/_us_states";

const service = new google.maps.places.AutocompleteService();

const CustomInputComponent = (props) => {
  if (props.select) {
    return (
      <Form.Field>
        <Form.Select
          value={props.value}
          label={props.label}
          onBlur={(e, d) =>
            props.onBlur({
              target: {
                name: d.name,
                value: d.value,
              },
            })
          }
          onChange={(e, d) =>
            props.onChange({
              target: {
                name: d.name,
                value: d.value,
              },
            })
          }
          options={props.options}
          placeholder="Select one..."
          name={props.name}
          search={true}
          required
        />
      </Form.Field>
    );
  }
  return (
    <Form.Field required>
      <Form.Input
        name={props.name}
        type="text"
        onBlur={props.onBlur}
        onChange={props.onChange}
        label={`${props.label}${!props.required ? " (optional)" : ""}`}
        value={props.value}
        required={props.required}
      />
    </Form.Field>
  );
};

export class AddressLookup extends Component {
  state = {
    addressData: null,
    address: "",
    auto: true,
    error: false,
    loading: false,
    results: [],
  };

  componentDidMount = () => {
    this.checkAutoPopulate();
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps !== this.props && this.state.auto) {
      this.checkAutoPopulate();
    }
  };

  checkAutoPopulate = () => {
    if (
      this.props.address1 &&
      this.props.city &&
      this.props.state &&
      this.props.zip
    ) {
      const address = `${this.props.address1} ${this.props.city}, ${this.props.state}`;
      this.setState({ address, loading: true });
      this.fetchResults(address);
    }
  };

  fetchResults = (address) => {
    service.getPlacePredictions(
      { input: address, componentRestrictions: { country: "us" } },
      (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          this.setState({
            error: null,
            loading: false,
            results: results.map((r) => ({
              ...r,
              description: null,
              key: r.id,
              title: r.description,
            })),
          });
        } else {
          this.setState({ loading: false, results: [] });
        }
      }
    );
  };

  handleChange = (e) => {
    const address = e.target.value;

    this.setState({
      address,
      error: null,
      loading: true,
    });

    if (address.length > 0) {
      this.setState({ loading: false, results: [] });
    }

    if (this.state.loading) {
      // return;
    }

    this.fetchResults(address);
  };

  handleSelect = (address, d) => {
    this.setState({ loading: true });
    geocodeByAddress(d.result.title)
      .then((results) => {
        if (!results.length) {
          return;
        }

        return { ...results[0] };
      })
      .then((result) => {
        return getLatLng(result)
          .then((l) => {
            return {
              ...result,
              ...l,
            };
          })
          .catch((err) => {
            console.log("error");
          });
      })
      .then((resultData) => {
        const addressComponents = resultData.address_components;
        const addressData = {
          street_number: null,
          street_name: null,
          city: null,
          county: null,
          state: null,
          zip: null,
          lat: resultData.lat,
          lng: resultData.lng,
        };

        const keysToCheck = [
          { google_key: "street_number", address_key: "street_number" },
          { google_key: "route", address_key: "street_name" },
          { google_key: "locality", address_key: "city" },
          { google_key: "administrative_area_level_2", address_key: "county" },
          {
            google_key: "administrative_area_level_1",
            address_key: "state",
            use_short_name: true,
          },
          { google_key: "postal_code", address_key: "zip" },
        ];

        keysToCheck.forEach((k) => {
          const keyData = addressComponents.find((a) =>
            a.types.includes(k.google_key)
          );

          if (!keyData) {
            if (k.address_key === "city") {
              let sublocality = addressComponents.find((a) =>
                a.types.includes("sublocality")
              );
              if (sublocality) {
                addressData.city = sublocality.short_name;
              }
            }
            return;
          }

          addressData[k.address_key] =
            keyData[k.use_short_name ? "short_name" : "long_name"];
        });

        if (addressData.state === "DC") {
          addressData.county = "n/a";
        }

        return addressData;
      })
      .then((result) => {
        this.setState({ address: d.result.title, loading: false, error: null });

        if (
          this.props.useTaxStates &&
          !stateTaxOptions.find((s) => s.value == result.state)
        ) {
          return this.props.onError(result.state);
        }

        return result;
      })
      .then((addressData) => {
        let error = null;
        Object.keys(addressData).forEach((k) => {
          if (!addressData[k]) {
            error = `We were unable to get sufficient address information for the address you selected. Please enter it manually`;
            return this.setState({
              address: "",
              error,
            });
          }
        });

        if (!error) {
          this.setState({ addressData });
          this.props.onSelect(addressData);
        }
      })
      .catch((error) => console.error("Error", error));
  };

  toggleAuto = (e) => {
    e.preventDefault();
    this.setState({
      auto: !this.state.auto,
      error: null,
    });

    if (!this.state.auto) {
      this.checkAutoPopulate();
    }
  };

  setError = (error) => this.setState({ error });

  render() {
    let fields = [
      {
        name: this.props.address1Name,
        value: this.props.address1,
        label: "Address",
        required: true,
        schema: () =>
          Yup.string()
            .min(2, "Too Short!")
            .max(50, "Too Long!")
            .required("Required"),
      },
      {
        name: this.props.address2Name,
        value: this.props.address2,
        label: "Address Line 2",
        schema: () => Yup.string(),
      },
      {
        name: this.props.cityName,
        value: this.props.city,
        label: "City",
        required: true,
        schema: () =>
          Yup.string()
            .min(2, "Too Short!")
            .max(50, "Too Long!")
            .required("Required"),
      },
      {
        name: this.props.stateName,
        value: this.props.state,
        label: "State",
        select: true,
        options: this.props.useTaxStates ? stateTaxOptions : usStates,
        required: true,
        search: true,
        schema: () => Yup.string().nullable().required("Required"),
      },
      {
        name: this.props.zipName,
        value: this.props.zip,
        label: "Zip",
        required: true,
        schema: () =>
          Yup.string()
            .min(5, "Too Short!")
            .max(5, "Too Long!")
            .required("Required"),
      },
    ];

    if (this.props.showCounty) {
      fields.push({
        name: "county",
        value: this.props.county,
        label: "County",
        required: true,
        schema: () =>
          Yup.string()
            .min(2, "Too Short!")
            .max(50, "Too Long!")
            .required("Required"),
      });
    }

    return (
      <div style={{ marginBottom: "20px" }}>
        {this.state.auto && (
          <Form.Field className="required">
            <label>{this.props.label}</label>
            <Search
              fluid
              loading={this.state.loading}
              onResultSelect={this.handleSelect}
              placeholder="Search for address..."
              onSearchChange={this.handleChange}
              results={this.state.results}
              size="small"
              value={this.state.address}
              // {...getInputProps()}
            />
            {this.state.error && (
              <p
                style={{ color: "red", fontWeight: "bold", marginTop: "10px" }}
              >
                {this.state.error}
              </p>
            )}
          </Form.Field>
        )}
        {!this.state.auto && (
          <React.Fragment>
            {fields.map((field, i) => (
              <CustomInputComponent
                label={field.label}
                onBlur={this.props.onBlur}
                options={field.options}
                onChange={this.props.onChange}
                key={i}
                name={field.name}
                required={field.required}
                search={field.search}
                select={field.select}
                value={field.value}
              />
            ))}
          </React.Fragment>
        )}
        <Button
          // content={this.state.auto ? 'Switch To Manual' : 'Switch To Auto'}
          size="tiny"
          onClick={this.toggleAuto}
          icon={this.state.auto ? "keyboard" : "search"}
          content={this.state.auto ? "Manual Entry" : "Auto Entry"}
          basic
        />
      </div>
    );
  }
}

AddressLookup.defaultProps = {
  address1Name: "address_1",
  address2Name: "address_2",
  cityName: "city",
  stateName: "state",
  zipName: "zip",
  formSize: "large",
  label: "Address",
  showCounty: true,
  useTaxStates: false,
};

AddressLookup.propTypes = {
  formSize: PropTypes.string,
  label: PropTypes.string,
};
